import React, { useState, useEffect } from 'react';
import { getGlobalSettings, updateGlobalSettings, subscribeToSettings } from '../services/settingsService';

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: 'facebook' },
  { id: 'twitter', name: 'Twitter', icon: 'twitter' },
  { id: 'instagram', name: 'Instagram', icon: 'instagram' },
  { id: 'youtube', name: 'YouTube', icon: 'youtube' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'linkedin' },
  { id: 'github', name: 'GitHub', icon: 'github' },
  { id: 'tiktok', name: 'TikTok', icon: 'tiktok' },
  { id: 'other', name: 'Other', icon: 'link' }
];

export default function FooterManagement() {
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form state for new link
  const [newPlatform, setNewPlatform] = useState('facebook');
  const [newUrl, setNewUrl] = useState('');

  useEffect(() => {
    const unsub = subscribeToSettings((data) => {
      setSocialLinks(data.socialLinks || []);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleAddLink = async (e) => {
    e.preventDefault();
    if (!newUrl) return;

    setSaving(true);
    const updatedLinks = [...socialLinks, { 
      id: Date.now().toString(), 
      platform: newPlatform, 
      url: newUrl 
    }];

    const success = await updateGlobalSettings({ socialLinks: updatedLinks });
    if (success) {
      setNewUrl('');
      setMessage({ type: 'success', text: 'Social link added successfully!' });
    } else {
      setMessage({ type: 'error', text: 'Failed to add social link.' });
    }
    setSaving(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;

    setSaving(true);
    const updatedLinks = socialLinks.filter(link => link.id !== id);
    const success = await updateGlobalSettings({ socialLinks: updatedLinks });
    
    if (success) {
      setMessage({ type: 'success', text: 'Social link removed!' });
    } else {
      setMessage({ type: 'error', text: 'Failed to remove link.' });
    }
    setSaving(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Footer Management</h1>
        <p className="text-slate-400 text-lg">Manage social media links displayed in your website footer.</p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 space-y-6">
        <h2 className="text-xl font-bold text-white uppercase tracking-wider">Add New Social Link</h2>
        <form onSubmit={handleAddLink} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-300 uppercase tracking-widest">Platform</label>
            <select
              value={newPlatform}
              onChange={(e) => setNewPlatform(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all appearance-none cursor-pointer"
            >
              {PLATFORMS.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 space-y-2">
            <label className="text-sm font-semibold text-slate-300 uppercase tracking-widest">URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="https://facebook.com/yourpage"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                required
                className="flex-1 px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
              />
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-blue-500/20 disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </form>

        {message.text && (
          <div className={`p-4 rounded-xl text-sm font-medium border ${
            message.type === 'success' 
              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
              : 'bg-red-500/10 text-red-400 border-red-500/20'
          }`}>
            {message.text}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white uppercase tracking-wider">Current Social Links</h2>
        <div className="grid grid-cols-1 gap-4">
          {socialLinks.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center text-slate-500">
              No social links added yet.
            </div>
          ) : (
            socialLinks.map((link) => (
              <div key={link.id} className="bg-slate-900 border border-slate-800 rounded-2xl p-4 flex items-center justify-between group hover:border-blue-500/30 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-blue-400">
                    {/* Placeholder icon based on platform */}
                    <span className="uppercase text-xs font-black">{link.platform.substring(0, 2)}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold capitalize">{link.platform}</h3>
                    <p className="text-slate-500 text-sm truncate max-w-[200px] md:max-w-md">{link.url}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteLink(link.id)}
                  className="p-3 bg-slate-800 hover:bg-red-600/20 hover:text-red-400 text-slate-400 rounded-xl transition-all"
                  title="Remove Link"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
